<script setup lang="ts">
/**
 * Tool strip — Draw · Erase · Trace · Colour · Brush size · Undo · Redo. The
 * only drawing-tool surface in the app (the top bar is brand/title/document
 * controls only).
 *
 * Brief 11-layout2 §1: position depends on orientation, not device category
 * — `side="bottom"` (portrait: a horizontal row under the top bar) or
 * `side="left"`/`"right"` (landscape: docks to one edge, vertical). The
 * caller (App.vue) decides which via useLayoutMode + the dominant-side
 * setting; this component only cares which edge it's ON, for its own
 * flex-direction and which way its colour popover opens.
 *
 * Sketch and Ink are no longer separate UI tools (brief: "remove the Sketch
 * vs Ink distinction from the UI... one primary drawing tool"). Draw always
 * targets a sketch-kind layer; Ink's own engine survives headless (it still
 * backs PSD-imported artwork), just with no manual toolbar entry point.
 * Bucket fill is hidden per the brief ("Filling is a painting workflow and
 * does not fit the product") — its code is untouched, just unreachable here.
 */
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import {
	IconArrowBackUp,
	IconArrowForwardUp,
	IconEraser,
	IconSketching,
	IconVectorBezier,
} from '@tabler/icons-vue'
import { BASE_PALETTE_SIZE, GRAPHITE_INDEX, paletteHex } from '@/patterns/wallpaper'
import { useLayersStore } from '@/stores/layers'
import { useToolsStore } from '@/stores/tools'
import { useTraceEditor } from '@/composables/useTraceEditor'

const props = withDefaults(defineProps<{ side?: 'bottom' | 'left' | 'right' }>(), { side: 'bottom' })

const layers = useLayersStore()
const tools = useToolsStore()
const trace = useTraceEditor()

// The pencil picker is a ring (brief 11-layout2 §3): 12 hues around the rim
// in cycle order — so opposite/triad/square/hex relationships are legible as
// literal geometric relationships on the ring — plus graphite as a distinct
// centre swatch, outside the rotating cycle. No custom hex, no editing
// individual generated colours: the popover only ever shows these 13 fixed
// swatches.
const PALETTE_INDICES = Array.from({ length: BASE_PALETTE_SIZE }, (_, i) => i)
const colourPickerOpen = ref(false)
const colourPickerRef = ref<HTMLElement | null>(null)

const RING_RADIUS = 62
const RING_SIZE = 168

/** Position for rim swatch `i` of 12, index 0 at the top, clockwise. */
function ringSwatchStyle(i: number): { left: string; top: string } {
	const angle = (i / BASE_PALETTE_SIZE) * 2 * Math.PI - Math.PI / 2
	const cx = RING_SIZE / 2 + RING_RADIUS * Math.cos(angle)
	const cy = RING_SIZE / 2 + RING_RADIUS * Math.sin(angle)
	return { left: `${cx}px`, top: `${cy}px` }
}

function pickColour(i: number): void {
	tools.selectColour(i)
	colourPickerOpen.value = false
}

function onDocClick(e: MouseEvent): void {
	if (colourPickerOpen.value && colourPickerRef.value && !colourPickerRef.value.contains(e.target as Node))
		colourPickerOpen.value = false
}

onMounted(() => document.addEventListener('click', onDocClick))
onBeforeUnmount(() => document.removeEventListener('click', onDocClick))

// One primary drawing tool (Draw) plus Erase and Trace — exactly the brief's
// three-tool row. Draw is active whenever neither of the other two is.
const isTrace = computed(() => layers.activeLayer === 'trace')
const isErase = computed(() => !isTrace.value && tools.activeTool === 'erase')
const isDraw = computed(() => !isTrace.value && !isErase.value)

function selectDraw(): void {
	// setMode returns to the last-used sketch layer, or creates one if none
	// exists yet.
	layers.setMode('sketch')
	tools.activeTool = 'pen'
}

function selectEraser(): void {
	// The eraser works on the active raster layer; never in vector trace mode.
	if (layers.activeLayer !== 'sketch' && layers.activeLayer !== 'ink') layers.setMode('sketch')
	tools.activeTool = 'erase'
}

function selectTrace(): void {
	layers.activeLayer = 'trace'
}

// Size selector — Draw (pencil) or Erase, each remembers its own size.
const sizeTarget = computed<'sketch' | 'erase' | null>(() =>
	isErase.value ? 'erase' : isDraw.value ? 'sketch' : null,
)
const sizeActive = computed(() => sizeTarget.value !== null)
const currentSize = computed(() => (sizeTarget.value === 'erase' ? tools.eraseSize : tools.sketchSize))
const SIZES = ['S', 'M', 'L'] as const

function setSize(s: (typeof SIZES)[number]): void {
	if (sizeTarget.value === 'erase') tools.eraseSize = s
	else if (sizeTarget.value === 'sketch') tools.sketchSize = s
}

/** True exactly when TraceFabs' own step-undo FAB is showing (same condition —
 *  see TraceFabs.vue) — an in-progress trace draft with at least one undoable
 *  tap. While both buttons are visible they must do the same thing, so the
 *  main Undo button routes to the draft's step-undo instead of the generic
 *  committed-layer undo, which knows nothing about an in-progress draft. */
const traceStepUndoActive = computed(
	() =>
		layers.activeLayer === 'trace' &&
		!!layers.editingTraceLayerId &&
		trace.mode.value === 'draw' &&
		trace.canUndoStep.value,
)

function undo(): void {
	if (traceStepUndoActive.value) {
		trace.undoStep()
		return
	}
	layers.undo()
}

function redo(): void {
	layers.redo()
}
</script>

<template>
	<div class="tool-strip" :class="`side-${props.side}`">
		<!-- Tools: Draw · Erase · Trace -->
		<div class="group seg">
			<button
				class="tb-btn"
				:class="{ active: isDraw }"
				title="Draw — textured pencil"
				@click="selectDraw"
			>
				<IconSketching :size="16" stroke-width="1.75" />
				<span>Draw</span>
			</button>
			<button
				class="tb-btn"
				:class="{ active: isErase }"
				title="Erase — erases pixels on the active layer"
				@click="selectEraser"
			>
				<IconEraser :size="16" stroke-width="1.75" />
				<span>Erase</span>
			</button>
			<button
				class="tb-btn"
				:class="{ active: isTrace }"
				title="Vector Trace — bezier outlines (Pro)"
				@click="selectTrace"
			>
				<IconVectorBezier :size="16" stroke-width="1.75" />
				<span>Trace</span>
			</button>
		</div>

		<!-- Colour — the pencil's colour, a ring of 12 hues plus a graphite
		     centre swatch (brief 11-layout2 §3: "I pick a pencil colour;
		     Escherllate handles the harmony"); shown whenever Draw is active. -->
		<div v-if="isDraw" ref="colourPickerRef" class="control colour-picker">
			<button
				class="swatch-btn"
				:style="{ backgroundColor: tools.color }"
				title="Pencil colour"
				aria-label="Pencil colour"
				:aria-expanded="colourPickerOpen"
				@click="colourPickerOpen = !colourPickerOpen"
			></button>
			<div v-if="colourPickerOpen" class="colour-ring" role="menu" :style="{ width: `${RING_SIZE}px`, height: `${RING_SIZE}px` }">
				<button
					v-for="i in PALETTE_INDICES"
					:key="i"
					class="ring-swatch"
					:class="{ active: tools.baseColourIndex === i }"
					:style="{ backgroundColor: paletteHex(i, tools.isDark), ...ringSwatchStyle(i) }"
					:title="`Pencil ${i + 1}`"
					:aria-label="`Pencil ${i + 1}`"
					@click="pickColour(i)"
				></button>
				<button
					class="ring-centre"
					:class="{ active: tools.baseColourIndex === GRAPHITE_INDEX }"
					:style="{ backgroundColor: paletteHex(GRAPHITE_INDEX, tools.isDark) }"
					title="Graphite"
					aria-label="Graphite"
					@click="pickColour(GRAPHITE_INDEX)"
				></button>
			</div>
		</div>

		<!-- Brush size — Draw or Erase -->
		<div class="group seg size-seg" :class="{ dim: !sizeActive }">
			<button
				v-for="s in SIZES"
				:key="s"
				class="tb-btn icon-only size-btn"
				:class="{ active: sizeActive && currentSize === s }"
				:disabled="!sizeActive"
				:title="`Size ${s}`"
				@click="setSize(s)"
			>
				<div class="size-dot" :class="`dot-${s}`"></div>
			</button>
		</div>

		<!-- Undo/redo -->
		<div class="group seg">
			<button
				class="tb-btn icon-only"
				:disabled="traceStepUndoActive ? false : !layers.canUndo"
				title="Undo"
				@click="undo"
			>
				<IconArrowBackUp :size="17" stroke-width="1.75" />
			</button>
			<button
				class="tb-btn icon-only"
				:disabled="!layers.canRedo"
				title="Redo"
				@click="redo"
			>
				<IconArrowForwardUp :size="17" stroke-width="1.75" />
			</button>
		</div>
	</div>
</template>

<style scoped>
.tool-strip {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	justify-content: flex-end;
	gap: 8px;
	padding: 6px 10px;
	background: var(--ui-surface);
	border-top: 1px solid var(--ui-border);
}

.tool-strip.side-left,
.tool-strip.side-right {
	flex-direction: column;
	flex-wrap: nowrap;
	height: 100%;
	padding: 10px 6px;
	border-top: none;
}

.tool-strip.side-left {
	border-right: 1px solid var(--ui-border);
}

.tool-strip.side-right {
	border-left: 1px solid var(--ui-border);
}

.group {
	display: flex;
	align-items: center;
	gap: 6px;
}

.side-left .group,
.side-right .group {
	flex-direction: column;
}

.group.seg {
	background: color-mix(in srgb, var(--ui-border) 35%, transparent);
	border: 1px solid var(--ui-border);
	border-radius: 9px;
	padding: 2px;
	gap: 2px;
}

.group.seg .tb-btn {
	border: none;
	border-radius: 7px;
}

.tb-btn {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	min-height: 32px;
	padding: 5px 11px;
	font-size: 12.5px;
	background: transparent;
	color: var(--text-primary);
	border: 1px solid var(--ui-border);
	border-radius: 8px;
	cursor: pointer;
	transition:
		border-color 0.12s,
		background 0.12s;
}

.tb-btn.icon-only {
	padding: 5px 8px;
}

.tb-btn:hover:not(:disabled) {
	border-color: var(--accent);
	background: color-mix(in srgb, var(--accent) 10%, transparent);
}

.tb-btn.active {
	background: color-mix(in srgb, var(--accent) 22%, transparent);
	color: var(--text-primary);
}

.tb-btn:disabled {
	opacity: 0.35;
	cursor: default;
}

.lock {
	color: var(--text-muted);
}

.control {
	display: flex;
	align-items: center;
	gap: 5px;
	color: var(--text-muted);
	font-family: var(--font-mono);
	font-size: 10px;
}

.colour-picker {
	position: relative;
}

.swatch-btn {
	width: 30px;
	height: 30px;
	padding: 0;
	border: 1px solid var(--ui-border);
	border-radius: 50%;
	cursor: pointer;
}

.colour-ring {
	position: absolute;
	top: 8px;
	left: 0;
	background: var(--ui-surface);
	border: 1px solid var(--ui-border);
	border-radius: 50%;
	box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.3);
	z-index: 20;
}

/* Docked to a side edge: the popover opens away from that edge, not upward
   (there may be no room above/below a slim vertical strip). */
.side-right .colour-ring {
	bottom: auto;
	left: auto;
	top: 0;
	right: calc(100% + 8px);
}

.side-left .colour-ring {
	bottom: auto;
	top: 0;
	left: calc(100% + 8px);
}

.size-seg.dim {
	opacity: 0.4;
}

.ring-swatch,
.ring-centre {
	position: absolute;
	transform: translate(-50%, -50%);
	border: 2px solid transparent;
	border-radius: 50%;
	padding: 0;
	cursor: pointer;
}

.ring-swatch {
	width: 26px;
	height: 26px;
}

.ring-centre {
	top: 50%;
	left: 50%;
	width: 40px;
	height: 40px;
	border-color: var(--ui-border);
}

.ring-swatch:hover,
.ring-centre:hover {
	border-color: var(--ui-border);
}

.ring-swatch.active,
.ring-centre.active {
	border-color: var(--accent);
}

.size-btn {
	min-width: 28px;
	justify-content: center;
	display: inline-flex;
	align-items: center;
}

.size-dot {
	display: inline-block;
	border-radius: 50%;
	background: currentColor;
}

.dot-S {
	width: 4px;
	height: 4px;
}

.dot-M {
	width: 8px;
	height: 8px;
}

.dot-L {
	width: 12px;
	height: 12px;
}

/* A slim edge column has no room for text labels regardless of width. */
.side-left .tb-btn span,
.side-right .tb-btn span {
	display: none;
}

@media (max-width: 550px) {
	.tool-strip.side-bottom {
		gap: 6px;
		padding: 4px 8px;
	}

	.side-bottom .tb-btn span {
		display: none;
	}
}
</style>
